/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ProductSelect } from '../ProductSelect';
import { InviteeList } from '../InviteeList';
import { ThirdPartyConnect, useThirdPartyState } from '../ThirdParty';
import { ViralSettings, useViralSettingsContext } from '../ViralSettings';
import { LineDivider } from '../Divider';
import { SlideAnimationWrapper } from '../SlideAnimationWrapper';
import { Recaptcha } from './Recaptcha';
import { messages } from '../i18n';
import { type InvitePeopleFormProps } from '../../types';
import { PluginRenderer } from './plugins';
import { useInvitePeopleContext } from '.';

const styles = cssMap({
	form: {
		marginTop: token('space.0', '0px'),
		marginBottom: token('space.300', '24px'),
	},
	formSection: {
		marginBottom: token('space.200', '16px'),
	},
});

const InvitePeopleForm: React.FC<InvitePeopleFormProps> = ({
	shouldRenderProductSelect,
	productOptions,
	handleProductSelectMenuOpen,
	showEmptyError,
	maxNumberOfInputs,
	onInviteeListChange,
	selectedProducts,
	showSecondarySection,
	recaptcha,
	setRecaptcha,
}) => {
	const {
		isOk: enableThirdPartyInvites,
		integrations: thirdPartyIntegrations,
		isConnected: thirdPartyConnected,
	} = useThirdPartyState();
	const { plugins, invitePromptOnLinkV3Cohort } = useInvitePeopleContext();
	const { showOpenInvite, eligibleDomains, isViralSettingsEnabled } = useViralSettingsContext();
	const showViralSettingsSection =
		isViralSettingsEnabled && (showOpenInvite || eligibleDomains.length > 0);
	const hasSecondarySection =
		!invitePromptOnLinkV3Cohort &&
		(shouldRenderProductSelect || showViralSettingsSection) &&
		showSecondarySection;

	const { formatMessage } = useIntl();

	const currentProduct = selectedProducts[0];

	const isCurrentProductInitiallyUndefined = useRef(currentProduct === undefined);

	const secondarySectionTitle = useMemo(() => {
		if (isCurrentProductInitiallyUndefined.current) {
			if (shouldRenderProductSelect && showViralSettingsSection) {
				return messages.secondarySectionHeadingNoCurrentProduct;
			} else if (shouldRenderProductSelect) {
				return messages.secondarySectionHeadingWithProductSelectorNoCurrentProduct;
			} else {
				return messages.secondarySectionHeadingWithViralSettings;
			}
		}

		if (shouldRenderProductSelect && showViralSettingsSection) {
			return messages.secondarySectionHeading;
		} else if (shouldRenderProductSelect) {
			return messages.secondarySectionHeadingWithProductSelector;
		} else {
			return messages.secondarySectionHeadingWithViralSettings;
		}
	}, [shouldRenderProductSelect, showViralSettingsSection]);

	return (
		<form
			onSubmit={(e: any) => {
				e.preventDefault();
			}}
			css={styles.form}
		>
			<Box xcss={styles.formSection}>
				<InviteeList
					maxEmails={maxNumberOfInputs}
					onInviteeListChange={onInviteeListChange}
					showEmptyError={showEmptyError}
				/>
			</Box>
			{enableThirdPartyInvites &&
				!invitePromptOnLinkV3Cohort &&
				thirdPartyIntegrations &&
				!thirdPartyConnected && (
					<Box xcss={styles.formSection}>
						<ThirdPartyConnect />
					</Box>
				)}
			<SlideAnimationWrapper shouldTriggerAnimation={hasSecondarySection}>
				<>
					<LineDivider />
					<Box xcss={styles.formSection}>
						<Heading size="xsmall" as="h2">
							{formatMessage(secondarySectionTitle)}
						</Heading>
					</Box>
					{shouldRenderProductSelect && (
						<Box xcss={styles.formSection}>
							<ProductSelect
								productOptions={productOptions}
								onMenuOpen={handleProductSelectMenuOpen}
								formInvalid={showEmptyError}
							/>
						</Box>
					)}
					{showViralSettingsSection && <ViralSettings product={selectedProducts[0]} />}
				</>
			</SlideAnimationWrapper>
			<PluginRenderer plugins={plugins} location="footer" />
			<Box xcss={styles.formSection}>
				<Recaptcha
					isLoading={!recaptcha?.isRecaptchaLoaded && !recaptcha?.recaptchaLoadErrorReason}
					onChange={setRecaptcha}
				/>
			</Box>
		</form>
	);
};

export default InvitePeopleForm;
